import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Phone, ChevronRight, ArrowRight, MapPin, Mail } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { CollectionSeoSection } from '@/components/sections/CollectionSeoSection';
import { useBreakpoint, useIsNarrowPhone } from '@/hooks/use-mobile';
import { fetchCollection, productDisplayPrice, type ShopifyProduct } from '@/lib/shopifyApi';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { SLUB_CONTENT } from '@/data/slubContent';

/* ─── Design tokens ──────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#1a1209';
const BROWN  = '#5a3e28';
const MUTED  = 'rgba(42,31,20,0.45)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';

const easeOut = [0.22, 1, 0.36, 1] as const;
const fadeUp  = (delay = 0) => ({
  initial:    { opacity: 0, y: 28 },
  whileInView:{ opacity: 1, y: 0 },
  viewport:   { once: true, margin: '-50px' },
  transition: { duration: 0.65, delay, ease: easeOut },
});

/* ─── Shared section header ──────────────────────────────────── */
function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginBottom: 20 }}>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>
        {text}
      </p>

    </div>
  );
}

/* ─── 1. HERO ────────────────────────────────────────────────── */
function Hero() {
  return (
    <section style={{ position: 'relative', minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', background: '#fff', overflow: 'hidden' }}>
      <PageBreadcrumb crumbs={[{ label: 'Ślub' }]} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1370, margin: '0 auto', padding: 'max(clamp(24px, 3vw, 48px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(24px, 3vw, 48px)' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: easeOut }}
          style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}
        >

          {/* decorative lines */}

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7, ease: easeOut }}
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 400, color: BROWN, marginBottom: 16, fontStyle: 'italic' }}
          >
            <CmsEditorialText id="information.hero.tagline" value={SLUB_CONTENT.hero.tagline} section="Wstęp" label="Nadtytuł" />
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: easeOut }}
            style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(2.2rem, 5.9vw, 5.4rem)',
              fontWeight: 400, lineHeight: 1.0, color: DARK,
              margin: '0 0 28px',
            }}
          >
            <CmsEditorialText id="information.hero.headingLine1" value={SLUB_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br /><CmsEditorialText id="information.hero.headingLine2Prefix" value={SLUB_CONTENT.hero.headingLine2Prefix} section="Wstęp" label="Nagłówek — początek drugiej linii" /><em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.hero.headingAccent" value={SLUB_CONTENT.hero.headingAccent} section="Wstęp" label="Wyróżniona część nagłówka" /></em><br /><CmsEditorialText id="information.hero.headingLine3" value={SLUB_CONTENT.hero.headingLine3} section="Wstęp" label="Nagłówek — trzecia linia" />
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: BROWN, lineHeight: 1.85, maxWidth: 540, marginBottom: 44, margin: '0 auto 44px' }}
          >
            <CmsEditorialText id="information.hero.body" value={SLUB_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: easeOut }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}
          >
            <a
              href="tel:+48605030728"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: '#fff', textDecoration: 'none', background: 'hsl(345 60% 22%)',
                padding: '15px 32px', transition: 'opacity 0.2s',
                cursor: 'pointer',
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.85')}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
              onTouchStart={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.85')}
              onTouchEnd={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
            >
              <Phone size={13} />
              <CmsEditorialText id="information.hero.ctaCallLabel" value={SLUB_CONTENT.hero.ctaCallLabel} section="Wstęp" label="Tekst przycisku" />
            </a>
            <a
              href="#realizacje"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: DARK, textDecoration: 'none',
                border: `1px solid ${GOLD}88`, padding: '15px 32px',
                transition: 'border-color 0.2s',
                cursor: 'pointer',
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.25)')}
              onTouchStart={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
              onTouchEnd={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.25)')}
            >
              <CmsEditorialText id="information.hero.ctaSeeLabel" value={SLUB_CONTENT.hero.ctaSeeLabel} section="Wstęp" label="Tekst przycisku" />
              <ChevronRight size={13} />
            </a>
          </motion.div>
        </motion.div>
      </div>

    </section>
  );
}

/* ─── 2. SERVICES ────────────────────────────────────────────── */
function ServicesSection() {
  const bp = useBreakpoint();
  const narrow = useIsNarrowPhone();

  const cols = bp === 'desktop'
    ? 'repeat(4, 1fr)'
    : narrow
      ? '1fr'
      : 'repeat(2, 1fr)';

  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 8vw, 112px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 64px)' }}>
          <SectionLabel text={SLUB_CONTENT.services.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem, 4vw, 3.2rem)', fontWeight: 400, lineHeight: 1.1, color: DARK, margin: '0 0 16px' }}>
            <CmsEditorialText id="information.services.heading" value={SLUB_CONTENT.services.heading} section="Usługi" label="Nagłówek" />
          </h2>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: MUTED, maxWidth: 540, margin: '0 auto', lineHeight: 1.75 }}>
            <CmsEditorialText id="information.services.body" value={SLUB_CONTENT.services.body} section="Usługi" label="Opis" multiline />
          </p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: 1,
          background: LINE,
        }}>
          {SLUB_CONTENT.services.items.map((s, i) => (
            <motion.div
              key={s.title}
              {...fadeUp(i * 0.06)}
              style={{
                background: '#fff',
                padding: bp === 'mobile' ? 'clamp(20px, 5vw, 28px)' : 'clamp(24px, 2.5vw, 36px)',
                borderTop: `2px solid ${GOLD}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <span style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 12, color: GOLD, letterSpacing: '0.1em',
              }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(16px, 1.8vw, 21px)',
                fontWeight: 400, color: DARK,
                margin: 0, lineHeight: 1.2,
              }}>
                <CmsEditorialText id={`information.services.items.${i}.title`} value={s.title} section="Usługi" label={`Element ${i + 1}: tytuł`} />
              </h3>
              <p style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13, color: MUTED,
                lineHeight: 1.72, margin: 0,
                flexGrow: 1,
              }}>
                <CmsEditorialText id={`information.services.items.${i}.desc`} value={s.desc} section="Usługi" label={`Element ${i + 1}: opis`} multiline />
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 4. REALIZACJE ──────────────────────────────────────────── */
function RealizacjeSection() {
  const bp = useBreakpoint();

  const cols = bp === 'desktop'
    ? 'repeat(3, 1fr)'
    : bp === 'tablet'
      ? 'repeat(2, 1fr)'
      : '1fr';

  return (
    <section id="realizacje" style={{ background: '#fff', padding: 'clamp(56px, 8vw, 112px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 64px)' }}>
          <SectionLabel text={SLUB_CONTENT.realizacje.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem, 4vw, 3.2rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.1 }}>
            <CmsEditorialText id="information.realizacje.heading" value={SLUB_CONTENT.realizacje.heading} section="Realizacje" label="Nagłówek" />
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 16, marginBottom: 40 }}>
          {SLUB_CONTENT.realizacje.items.map((r, i) => (
            <motion.div key={i} {...fadeUp(i * 0.12)}
              style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }}
            >
              {/* Photo placeholder — swap with real img when available */}
              <div style={{
                background: '#fff',
                /* On mobile single-col, use 16/9 for middle card to avoid excessive height */
                aspectRatio: bp === 'mobile' && i === 1 ? '16/9' : i === 1 ? '3/4' : '4/5',
                position: 'relative',
                overflow: 'hidden',
              }}>


                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '28px 24px' }}>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 }}>
                    <CmsEditorialText id={`information.realizacje.items.${i}.style`} value={r.style} section="Realizacje" label={`Element ${i + 1}: styl`} />
                  </p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, margin: 0, lineHeight: 1.5 }}>
                    <CmsEditorialText id={`information.realizacje.items.${i}.scope`} value={r.scope} section="Realizacje" label={`Element ${i + 1}: zakres`} />
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp(0.36)} style={{ textAlign: 'center' }}>
          <a
            href="tel:+48605030728"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: DARK, textDecoration: 'none', border: `1px solid ${GOLD}`, padding: '13px 28px',
              transition: 'background 0.25s, color 0.25s',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = GOLD; el.style.color = '#fff'; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = 'transparent'; el.style.color = DARK; }}
            onTouchStart={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = GOLD; el.style.color = '#fff'; }}
            onTouchEnd={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = 'transparent'; el.style.color = DARK; }}
          >
            <CmsEditorialText id="information.realizacje.ctaLabel" value={SLUB_CONTENT.realizacje.ctaLabel} section="Realizacje" label="Tekst przycisku" />
            <ArrowRight size={13} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── 5. ADVISORY ────────────────────────────────────────────── */
const CONTACT_ROW_ICON_MAP: Record<string, React.ReactNode> = {
  mapPin: <MapPin size={13} color={GOLD} />,
  mail: <Mail size={13} color={GOLD} />,
  phone: <Phone size={13} color={GOLD} />,
};

function AdvisorySection() {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';

  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 8vw, 112px) 0', position: 'relative', overflow: 'hidden' }}>
      {/* Background accents */}

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 36 : 'clamp(40px, 6vw, 96px)',
          alignItems: 'center',
        }}>

          {/* Left: quote */}
          <motion.div {...fadeUp()}>
            <p style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1rem, 2.2vw, 1.55rem)', fontStyle: 'italic', fontWeight: 400, color: DARK, lineHeight: 1.75, margin: '0 0 28px' }}>
              <CmsEditorialText id="information.advisory.quote" value={SLUB_CONTENT.advisory.quote} section="Kontakt i porady" label="Cytat" multiline />
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.14em', color: GOLD }}><CmsEditorialText id="information.advisory.quoteAttribution" value={SLUB_CONTENT.advisory.quoteAttribution} section="Kontakt i porady" label="Podpis cytatu" multiline /></span>
            </div>
          </motion.div>

          {/* Right: CTA card */}
          <motion.div {...fadeUp(0.15)} style={{ border: `1px solid ${GOLD}66`, padding: isMobile ? 'clamp(24px, 6vw, 36px)' : 'clamp(32px, 4vw, 52px)', background: '#fff' }}>
            <SectionLabel text={SLUB_CONTENT.advisory.contactEyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.7rem, 3vw, 2.8rem)', fontWeight: 400, color: DARK, margin: '0 0 20px', lineHeight: 1.1 }}>
              <CmsEditorialText id="information.advisory.contactHeadingLine1" value={SLUB_CONTENT.advisory.contactHeadingLine1} section="Kontakt i porady" label="Nagłówek kontaktu — pierwsza linia" /><br /><CmsEditorialText id="information.advisory.contactHeadingLine2" value={SLUB_CONTENT.advisory.contactHeadingLine2} section="Kontakt i porady" label="Nagłówek kontaktu — druga linia" />
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: BROWN, lineHeight: 1.8, marginBottom: 32 }}>
              <CmsEditorialText id="information.advisory.contactBody" value={SLUB_CONTENT.advisory.contactBody} section="Kontakt i porady" label="Opis kontaktu" multiline />
            </p>

            {/* Contact details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderTop: `1px solid ${GOLD}44` }}>
              {SLUB_CONTENT.advisory.contactRows.map(row => (
                <a
                  key={row.label}
                  href={row.href}
                  target={row.href.startsWith('http') ? '_blank' : undefined}
                  rel={row.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 0',
                    borderBottom: `1px solid ${GOLD}44`,
                    textDecoration: 'none',
                    transition: 'opacity 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.7')}
                  onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
                  onTouchStart={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.7')}
                  onTouchEnd={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    border: `1px solid ${GOLD}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {CONTACT_ROW_ICON_MAP[row.iconKey]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, margin: '0 0 4px' }}>
                      {row.label}
                    </p>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? 17 : 20, fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── 6. PROCESS ─────────────────────────────────────────────── */
function ProcessSection() {
  const bp = useBreakpoint();
  const narrow = useIsNarrowPhone();
  const isDesktop = bp === 'desktop';

  const cols = isDesktop
    ? 'repeat(4, 1fr)'
    : narrow
      ? '1fr'
      : 'repeat(2, 1fr)';

  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(56px, 8vw, 112px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>

        {/* ── Heading ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 64px)' }}
        >
          <SectionLabel text={SLUB_CONTENT.process.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem, 4vw, 3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.process.heading" value={SLUB_CONTENT.process.heading} section="Jak to działa" label="Nagłówek" />
          </h2>
        </motion.div>

        {/* ── Steps ── */}
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: isDesktop ? 0 : 'clamp(24px, 4vw, 40px)', position: 'relative' }}>

          {/* Connecting line — animated, desktop only */}

          {SLUB_CONTENT.process.steps.map((p, i) => (
            <motion.div
              key={p.num}
              initial={{ opacity: 0, y: 28 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: isDesktop ? 'clamp(20px, 2vw, 32px) clamp(12px, 2.5vw, 28px)' : '0',
                textAlign: 'center',
                position: 'relative', zIndex: 1,
              }}
            >
              {/* Icon circle — hover spring */}
              <motion.div
                variants={{
                  hover: {
                    scale: 1.13,
                    boxShadow: '0 8px 32px rgba(210,60,120,0.48)',
                  },
                }}
                whileHover="hover"
                transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                style={{
                  width: 76, height: 76, borderRadius: '50%',
                  border: `1.5px solid ${GOLD}`,
                  background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 30,
                  marginBottom: 20,
                  boxShadow: `0 4px 20px rgba(210,60,120,0.22)`,
                  position: 'relative',
                  cursor: 'default',
                }}
              >
                {/* Emoji — rotates on hover */}
                <motion.span
                  variants={{ hover: { rotate: 14, scale: 1.18 } }}
                  transition={{ type: 'spring', stiffness: 380, damping: 14 }}
                  style={{ display: 'inline-block', lineHeight: 1 }}
                >
                  {p.emoji}
                </motion.span>

                {/* Step number badge */}
                <span aria-hidden style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 22, height: 22, borderRadius: '50%',
                  background: GOLD,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 9, fontWeight: 600, color: '#fff',
                  letterSpacing: '0.02em',
                }}>
                  {i + 1}
                </span>
              </motion.div>

              {/* Step label */}
              <span aria-hidden style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11, letterSpacing: '0.25em',
                color: GOLD, textTransform: 'uppercase',
                marginBottom: 10, fontWeight: 500,
                display: 'block',
              }}>
                Krok {p.num}
              </span>

              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.1rem, 1.5vw, 1.35rem)', fontWeight: 600, color: DARK, margin: '0 0 12px', lineHeight: 1.25 }}>
                <CmsEditorialText id={`information.process.steps.${i}.title`} value={p.title} section="Jak to działa" label={`Element ${i + 1}: tytuł`} />
              </h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(12.5px, 1.1vw, 14px)', fontWeight: 400, color: BROWN, lineHeight: 1.65, margin: 0, maxWidth: '22ch', marginInline: 'auto' }}>
                <CmsEditorialText id={`information.process.steps.${i}.desc`} value={p.desc} section="Jak to działa" label={`Element ${i + 1}: opis`} multiline />
              </p>

              {/* Arrow connector — desktop, not last */}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ─── Skeleton ───────────────────────────────────────────────── */
function BridalSkeleton() {
  return (
    <div className="bridal-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i}>
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)' }} className="animate-pulse" />
          <div style={{ padding: '16px 18px 20px', background: '#fff' }}>
            <div style={{ height: 18, background: 'rgba(210,60,120,0.07)', marginBottom: 8, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 12, width: '50%' }} className="animate-pulse" />
            <div style={{ height: 17, background: 'rgba(210,60,120,0.07)', width: '35%' }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Bukiety Panny Młodej ───────────────────────────────────── */
function BridalBouquetsSection() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading,  setLoading]  = useState(true);
  usePrerenderReady(!loading);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchCollection('slubne', { sort: 'COLLECTION_DEFAULT', reverse: false, limit: 100 })
      .then(col => {
        if (!col) { setNotFound(true); return; }
        setProducts((col.products.nodes ?? []).map(p => ({ ...p, tags: p.tags ?? [] })));
      })
      .catch(err => {
        // A transport failure is not a missing collection. Keep any products
        // already shown and leave the 404 state untouched.
        console.warn('[BridalBouquetsSection] catalog refresh failed:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  /* Hide entirely if collection not found */
  if (!loading && notFound) return null;

  return (
    <section style={{ background: '#ffffff', padding: 'clamp(56px,8vw,112px) 0' }}>
      <style>{`
        .bridal-grid { grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 768px)  { .bridal-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>

      <div style={{ padding: '0' }}>

        {/* ── Heading ── */}
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,64px)' }}>
          <SectionLabel text={SLUB_CONTENT.bridalBouquets.eyebrow} />
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(1.9rem,4vw,3.2rem)', fontWeight: 400,
            lineHeight: 1.1, color: DARK, margin: '0 0 16px',
          }}>
            <CmsEditorialText id="information.bridalBouquets.heading" value={SLUB_CONTENT.bridalBouquets.heading} section="Bukiety ślubne" label="Nagłówek" />
          </h2>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: MUTED,
            maxWidth: 500, margin: '0 auto', lineHeight: 1.75,
          }}>
            <CmsEditorialText id="information.bridalBouquets.body" value={SLUB_CONTENT.bridalBouquets.body} section="Bukiety ślubne" label="Opis" multiline />
          </p>
        </motion.div>

        {/* ── Grid ── */}
        {loading && <BridalSkeleton />}

        {!loading && products.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, color: DARK }}>
              Brak produktów w tej kategorii
            </p>
          </div>
        )}

        {!loading && products.length > 0 && (
          <>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, marginBottom: 20, letterSpacing: '0.04em' }}>
              {products.length} {products.length === 1 ? 'bukiet' : products.length < 5 ? 'bukiety' : 'bukietów'} w ofercie
            </p>
            <div className="bridal-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
              {products.map((product, i) => (
                <CollectionProductCard
                  key={product.handle}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={{ label: 'Bukiety Panny Młodej', href: '/slub/' }}
                  centerInfo
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function SlubPage() {
  useSeo(PAGE_SEO.slub);
  return (
    <div className="min-h-[100dvh]" style={{ background: '#fff' }}>
      <main>
        <Hero />
        <BridalBouquetsSection />
        <ServicesSection />
        <RealizacjeSection />
        <ProcessSection />
        <AdvisorySection />
      </main>
      <CollectionSeoSection handle="slub" />
      <Footer />
    </div>
  );
}
